import { useState, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Share2, Copy, X, Zap, Trophy, BookOpen, Flame, Brain, Star } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

export default function ShareProgress({ api }) {
  const [showModal, setShowModal] = useState(false);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const cardRef = useRef(null);

  const handleShare = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${api}/share/stats`, { withCredentials: true });
      setStats(res.data);
      setShowModal(true);
    } catch {
      toast.error("Failed to load stats");
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = () => {
    if (!stats) return;
    const text = `PhD Reader Progress\n${"=".repeat(25)}\nLevel ${stats.level} | ${stats.total_xp} XP\n${stats.papers_completed} papers completed\n${stats.total_pages_read} pages read\n${stats.current_streak} day streak\n${stats.badges_count} badges earned\nRank #${stats.rank} of ${stats.total_users}\n\nJoin me on PhD Reader!`;
    navigator.clipboard.writeText(text).then(() => {
      toast.success("Progress copied to clipboard!");
    }).catch(() => {
      toast.error("Failed to copy");
    });
  };

  const shareNative = async () => {
    if (!stats || !navigator.share) {
      copyToClipboard();
      return;
    }
    try {
      await navigator.share({
        title: "My PhD Reader Progress",
        text: `Level ${stats.level} | ${stats.total_xp} XP | ${stats.papers_completed} papers | ${stats.current_streak} day streak on PhD Reader!`,
      });
    } catch {
      // User cancelled
    }
  };

  return (
    <>
      <button
        onClick={handleShare}
        disabled={loading}
        className="neo-btn bg-white dark:bg-[#2A2A2A] dark:border-[#444] dark:text-white flex items-center gap-2 text-sm"
        data-testid="share-progress-btn"
      >
        <Share2 className="w-4 h-4" />
        <span className="hidden sm:inline">Share</span>
      </button>

      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="neo-card dark:bg-[#1E1E1E] dark:border-[#444] dark:shadow-[6px_6px_0px_0px_#444] border-[3px] border-neo-border shadow-[6px_6px_0px_0px_#1A1A1A] max-w-sm" data-testid="share-modal">
          <DialogHeader>
            <DialogTitle className="font-heading font-extrabold text-xl dark:text-white flex items-center gap-2">
              <Share2 className="w-5 h-5 text-neo-blue" />
              Share Progress
            </DialogTitle>
          </DialogHeader>

          {stats && (
            <div className="space-y-4">
              {/* Shareable Card */}
              <div
                ref={cardRef}
                className="bg-neo-yellow rounded-xl p-5 border-[3px] border-neo-border shadow-[4px_4px_0px_0px_#1A1A1A]"
                data-testid="share-card"
              >
                <div className="flex items-center gap-2 mb-4">
                  <BookOpen className="w-6 h-6 text-neo-text" />
                  <span className="font-heading font-extrabold text-lg">PhD Reader</span>
                </div>
                <h3 className="font-heading font-extrabold text-2xl mb-1">{stats.name}</h3>
                <p className="text-sm font-bold mb-4">
                  Rank #{stats.rank} of {stats.total_users}
                </p>
                <div className="grid grid-cols-2 gap-3">
                  <div className="bg-white/60 rounded-lg p-2 text-center border-2 border-neo-border/30">
                    <div className="flex items-center justify-center gap-1 mb-1">
                      <Zap className="w-4 h-4" />
                    </div>
                    <p className="font-heading font-extrabold text-xl">{stats.total_xp}</p>
                    <p className="text-xs font-semibold">XP (Lv {stats.level})</p>
                  </div>
                  <div className="bg-white/60 rounded-lg p-2 text-center border-2 border-neo-border/30">
                    <div className="flex items-center justify-center gap-1 mb-1">
                      <BookOpen className="w-4 h-4" />
                    </div>
                    <p className="font-heading font-extrabold text-xl">{stats.papers_completed}</p>
                    <p className="text-xs font-semibold">Papers</p>
                  </div>
                  <div className="bg-white/60 rounded-lg p-2 text-center border-2 border-neo-border/30">
                    <div className="flex items-center justify-center gap-1 mb-1">
                      <Flame className="w-4 h-4" />
                    </div>
                    <p className="font-heading font-extrabold text-xl">{stats.current_streak}</p>
                    <p className="text-xs font-semibold">Day Streak</p>
                  </div>
                  <div className="bg-white/60 rounded-lg p-2 text-center border-2 border-neo-border/30">
                    <div className="flex items-center justify-center gap-1 mb-1">
                      <Star className="w-4 h-4" />
                    </div>
                    <p className="font-heading font-extrabold text-xl">{stats.badges_count}</p>
                    <p className="text-xs font-semibold">Badges</p>
                  </div>
                </div>
              </div>

              <div className="flex gap-2">
                <button
                  onClick={copyToClipboard}
                  className="neo-btn bg-white dark:bg-[#2A2A2A] dark:border-[#444] dark:text-white flex-1 flex items-center justify-center gap-2"
                  data-testid="copy-stats-btn"
                >
                  <Copy className="w-4 h-4" />
                  Copy Text
                </button>
                <button
                  onClick={shareNative}
                  className="neo-btn neo-btn-primary flex-1 flex items-center justify-center gap-2"
                  data-testid="native-share-btn"
                >
                  <Share2 className="w-4 h-4" />
                  Share
                </button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
